import { useEffect, useState } from 'react';

import { getUser, MentionApiError } from '../api';
import { useMentionContextOptional } from '../MentionProvider';
import type { UserDetails } from '../types';

export interface UseMentionUserResult {
  user: UserDetails | null;
  isLoading: boolean;
  error: string | null;
}

// ---------------------------------------------------------------------------
// Module-level cache
// ---------------------------------------------------------------------------
// WHY: In a comment feed, dozens of <MentionChip />s may render the same user.
// Without this cache, every mount triggers a network request — even though the
// backend IdentityService already caches upstream AC results. With it:
//   1. Only ONE request per (entityId, userId) across all chips on the page
//   2. In-flight requests are deduped: 5 chips mounting simultaneously share 1 promise
//   3. Remounts within TTL are instant (0 network calls)
// Keyed by `${entityId}:${userId}` so switching tenants doesn't return stale data.

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const CACHE_CAPACITY = 500;

interface CacheEntry {
  user: UserDetails;
  fetchedAt: number;
}

const userCache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<UserDetails>>();

function cacheKey(entityId: string, userId: string): string {
  return `${entityId}:${userId}`;
}

function evictIfFull(): void {
  // Use a `while` loop so the cache returns to capacity even if a future
  // caller bulk-inserts entries between calls. Map.keys() yields in insertion
  // order, so the first key is the oldest (FIFO eviction).
  while (userCache.size > CACHE_CAPACITY) {
    const oldest = userCache.keys().next().value;
    if (oldest === undefined) return;
    userCache.delete(oldest);
  }
}

/**
 * Fetch user details with shared cache + in-flight dedup.
 * Exported for advanced callers (e.g. warming the cache before rendering chips).
 */
export async function fetchMentionUser(
  baseUrl: string,
  getAccessToken: () => Promise<string>,
  entityId: string,
  userId: string,
): Promise<UserDetails> {
  const key = cacheKey(entityId, userId);
  const cached = userCache.get(key);
  if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
    return cached.user;
  }

  const existing = inflight.get(key);
  if (existing) return existing;

  const promise = (async () => {
    const token = await getAccessToken();
    const user = await getUser(baseUrl, token, entityId, userId);
    userCache.set(key, { user, fetchedAt: Date.now() });
    evictIfFull();
    return user;
  })();

  inflight.set(key, promise);
  try {
    return await promise;
  } finally {
    inflight.delete(key);
  }
}

/**
 * Clear the frontend user-details cache. Useful on logout / tenant switch
 * when the caller knows cached data may be stale.
 */
export function clearMentionUserCache(): void {
  userCache.clear();
  inflight.clear();
}

/**
 * React hook that resolves a user's details by ID using the MentionProvider
 * context. Returns `{ user: null, isLoading: false }` when `userId` is empty
 * or when `entityId` is not available (from either the `entityId` argument or
 * the MentionProvider context).
 *
 * Deactivated users resolve successfully with `user.disabled === true`.
 * Genuine errors (network/4xx/5xx) surface via `error` with `user === null`.
 */
export function useMentionUser(
  userId: string | undefined,
  entityIdOverride?: string,
): UseMentionUserResult {
  const ctx = useMentionContextOptional();
  const baseUrl = ctx?.baseUrl;
  const getAccessToken = ctx?.getAccessToken;
  const entityId = entityIdOverride ?? ctx?.entityId;
  const canFetch = Boolean(userId && entityId && baseUrl && getAccessToken);

  const [state, setState] = useState<UseMentionUserResult>(() => {
    if (!userId || !entityId) return { user: null, isLoading: false, error: null };
    const cached = userCache.get(cacheKey(entityId, userId));
    if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
      return { user: cached.user, isLoading: false, error: null };
    }
    return { user: null, isLoading: canFetch, error: null };
  });

  useEffect(() => {
    if (!userId || !entityId || !baseUrl || !getAccessToken) {
      setState({ user: null, isLoading: false, error: null });
      return;
    }

    const cached = userCache.get(cacheKey(entityId, userId));
    if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
      setState({ user: cached.user, isLoading: false, error: null });
      return;
    }

    let cancelled = false;
    setState((s) => ({ user: s.user, isLoading: true, error: null }));

    fetchMentionUser(baseUrl, getAccessToken, entityId, userId)
      .then((user) => {
        if (cancelled) return;
        setState({ user, isLoading: false, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message =
          err instanceof MentionApiError ? err.message : 'Unable to load user details';
        setState({ user: null, isLoading: false, error: message });
      });

    return () => {
      cancelled = true;
    };
  }, [baseUrl, getAccessToken, entityId, userId]);

  return state;
}
